import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import type { AxiosError } from 'axios'
import axios from 'axios'
import { isObjectLike } from 'lodash-es'
import qs from 'qs'

import { TOAST_STR } from '@/constant'
import { CommonError } from '@/dto'
import WebviewService from '@/service/webview.service'
import type { ResErr } from '@/types/common/api'
import { reporter } from '@/utils/modules/errorReporting/reporter'

import { deleteNullField } from '../common'
import { envUtils } from '../envUtils'
import { toastError } from '../toast'
import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from './jwtInterceptors'

const BASE_URL = (() => {
  if (envUtils.isLive) {
    return `https://pod-api.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isStaging) {
    return `https://pod-api-staging.${domainProvider.TARGET_DOMAIN}`
  }

  return `https://pod-api-dev.${domainProvider.TARGET_DOMAIN}`
})()

const XPlatform = (() => {
  if (WebviewService.isAndroid) return 'TEACHER_ANDROID'
  if (WebviewService.isIpad) return 'TEACHER_IOS'
  return 'TEACHER_WEB'
})()

const podAxios = axios.create({
  baseURL: BASE_URL,
  headers: {
    'x-platform': XPlatform,
    'x-freewheelin-host': envUtils.isSchool ? 'schoolflat.com' : domainProvider.TARGET_DOMAIN,
  },
  paramsSerializer: (params) => {
    // params가 null인 값은 서버로 보내지 않음
    const newParams = isObjectLike(params) ? deleteNullField(params) : params
    return qs.stringify(newParams, { arrayFormat: 'comma' })
  },
})

podAxios.interceptors.request.use(wrapRequestInterceptorWithJwt((config) => config))

const podJwtErrorHandler = wrapErrorHandlerWithJwt(
  async (error: unknown) => {
    const axiosError = error as AxiosError<ResErr>

    if (axiosError.response) {
      const {
        config,
        data: { message, code, data },
        status,
      } = axiosError.response
      // 결제·주문은 status 와 무관하게 항상 보고한다(D-07). 파이프라인이 flow 를 보고 드롭 규칙을 건너뛴다.
      const err = new CommonError(message, {
        type: 'API',
        status,
        code,
        data,
        config,
        flow: 'payment',
      })

      if (status === 500) {
        toastError(TOAST_STR.errorMessage)
      }
      reporter.capture(err, { mechanism: 'interceptor' })
      return Promise.reject<CommonError>(err)
    }

    // 응답 없는 실패는 날것 그대로 던지므로 결제 표시를 객체에 실을 수 없다 — 컨텍스트로 넘긴다
    reporter.capture(axiosError, { mechanism: 'interceptor', flow: 'payment' })
    return Promise.reject(axiosError)
  },
  async (originalRequest, newAccessToken) => {
    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
    return podAxios(originalRequest)
  },
)

podAxios.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const axiosError = error as AxiosError
    // POD 서버가 401 응답 시 CORS 헤더를 포함하지 않아
    // 브라우저가 응답을 차단하면 ERR_NETWORK로 처리됨.
    // JWT retry 로직이 작동하도록 가짜 401 응답으로 변환
    if (!axiosError.response && axiosError.code === 'ERR_NETWORK') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(axiosError as any).response = {
        status: 401,
        statusText: 'Unauthorized',
        data: { message: 'Network Error', code: 'ERR_NETWORK', data: null },
        headers: {},
        config: axiosError.config,
      }
    }
    return podJwtErrorHandler(error)
  },
)

export default podAxios
